import { useEffect, useRef, useState } from "react";
import { ServicesRow } from "@/components/ServicesRow";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Sparkles, ShieldCheck, Truck, ChevronLeft, ChevronRight, Users, MapPin, Star, Instagram, Facebook, Youtube, MessageCircle, Quote, ImagePlus, X, Crown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { bust, thumb } from "@/lib/img";
import ServiceAnnouncementBar from "@/components/ServiceAnnouncementBar";
import { InstagramSection, OfficeSection } from "@/components/HomeSections";
import RealWeddings from "@/components/RealWeddings";
import DestinationWedding from "@/components/DestinationWedding";
import LazyMount from "@/components/LazyMount";
import { requestJourneyPopup } from "@/components/JourneyPopup";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const COLLECTIONS = [
  { key: "wedding", title: "Wedding", tag: "10 rituals · 500+ designs", img: "https://images.unsplash.com/photo-1587271407850-8d438ca9fdf2?w=1200" },
  { key: "once_a_year", title: "Once A Year", tag: "Birthday · Anniversary · Baby Shower · Festival", img: "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=1200" },
  { key: "tent_items", title: "Tent Items", tag: "8 sections · 80 essentials", img: "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=1200" },
  { key: "packages", title: "Packages", tag: "Jaipur · Pushkar · Udaipur · Jaisalmer · Ranthambore", img: "https://images.pexels.com/photos/35338836/pexels-photo-35338836.jpeg?auto=compress&cs=tinysrgb&w=1200" },
];const GALLERY = [
  { src: "https://images.unsplash.com/photo-1761472606347-bfebc5a3e546?w=2000", caption: "Ornate mandap · Jaipur wedding" },
  { src: "https://images.unsplash.com/photo-1587271407850-8d438ca9fdf2?w=2000", caption: "Traditional saat pheras" },
  { src: "https://images.unsplash.com/photo-1708606811579-23b18fc48007?w=2000", caption: "Grand reception stage" },
  { src: "https://images.pexels.com/photos/35338836/pexels-photo-35338836.jpeg?auto=compress&cs=tinysrgb&w=2000", caption: "Lake Palace, Udaipur" },
  { src: "https://images.unsplash.com/photo-1636996627212-ce5d24bca63a?w=2000", caption: "Hawa Mahal, Jaipur" },
  { src: "https://images.pexels.com/photos/16014507/pexels-photo-16014507.jpeg?auto=compress&cs=tinysrgb&w=2000", caption: "Golden fort, Jaisalmer" },
  { src: "https://images.unsplash.com/photo-1519741497674-611481863552?w=2000", caption: "Sangeet night" },
];export default function Home() {
  const [slide, setSlide] = useState(0);
  const [guests, setGuests] = useState(150);
  const [city, setCity] = useState("jaipur");
  const [reviews, setReviews] = useState([]);
  const [social, setSocial] = useState({ instagram: "", facebook: "", whatsapp: "", youtube: "" });
  const [collectionTiles, setCollectionTiles] = useState({});
  const [tilesLoaded, setTilesLoaded] = useState(false);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [heroSettings, setHeroSettings] = useState({ video_url: "", slides: [], _v: "" });
  const [galleryLoaded, setGalleryLoaded] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [insta, setInsta] = useState({ handle: "", posts: [] });
  const [office, setOffice] = useState(null);
  const [reviewForm, setReviewForm] = useState({ name: "", rating: 5, message: "", city: "", photo_url: "", social_url: "" });
  const [submitting, setSubmitting] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [openReview, setOpenReview] = useState(null);
  const reviewFileRef = useRef(null);
  const nav = useNavigate();

  const [belowFold, setBelowFold] = useState(false);

  // Above-the-fold only: hero + collection tiles. Everything else waits until the
  // visitor scrolls near it (see LazyMount below).
  useEffect(() => {
    api.get("/settings/collections").then((r) => setCollectionTiles(r.data || {})).catch(() => {}).finally(() => setTilesLoaded(true));
    api.get("/settings/gallery")
      .then((r) => {
        const list = (r.data || []).map((p) => ({ src: p.url, caption: p.caption, v: p.updated_at || p.created_at }));
        if (list.length) setGalleryPhotos(list);
      })
      .catch(() => {})
      .finally(() => setGalleryLoaded(true));
    api.get("/settings/hero")
      .then((r) => setHeroSettings(r.data || { video_url: "", slides: [], _v: "" }))
      .catch(() => {})
      .finally(() => setHeroLoaded(true));
  }, []);
  useEffect(() => {
    if (!belowFold) return;
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/settings/social").then((r) => setSocial(r.data)).catch(() => {});
    api.get("/settings/instagram").then((r) => setInsta(r.data || { handle: "", posts: [] })).catch(() => {});
    api.get("/settings/office").then((r) => setOffice(r.data || null)).catch(() => {});
  }, [belowFold]);

  // Admin-uploaded gallery photos are the source of truth for the hero slider.
  // Fall back to admin's hero-slide list only when no gallery photos exist,
  // and to the built-in GALLERY constant only when both are empty.
  // We also wait for BOTH endpoints to resolve before computing the list so
  // the user never sees one source briefly and then a swap to another.
  const heroVideo = (heroSettings.video_url || "").trim();
  const heroSlides = (heroSettings.slides || []).filter((s) => s && (s.url || "").trim()).map((s) => ({ src: s.url, caption: s.caption || "", v: heroSettings._v }));
  const slidersReady = galleryLoaded && heroLoaded;
  const galleryList = slidersReady
    ? (galleryPhotos.length ? galleryPhotos : (heroSlides.length ? heroSlides : GALLERY))
    : [];

  useEffect(() => {
    if (galleryList.length <= 1) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % galleryList.length), 5000);
    return () => clearInterval(t);
  }, [galleryList.length]);
  const prev = () => setSlide((s) => (s - 1 + galleryList.length) % galleryList.length);
  const next = () => setSlide((s) => (s + 1) % galleryList.length);

  const submitRecommend = (e) => {
    e.preventDefault();
    nav(`/recommend?city=${city}&guests=${guests}`);
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.name.trim() || !reviewForm.message.trim()) {
      toast.error("Please share your name and a short message");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", reviewForm);
      toast.success("Thanks! Your review is queued for approval.");
      setReviewForm({ name: "", rating: 5, message: "", city: "", photo_url: "", social_url: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit review");
    } finally {
      setSubmitting(false);
    }
  };

  const uploadReviewPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/reviews/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReviewForm((f) => ({ ...f, photo_url: r.data.url }));
      toast.success("Photo added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Photo upload failed");
    } finally {
      setPhotoUploading(false);
      if (reviewFileRef.current) reviewFileRef.current.value = "";
    }
  };

  return (
    <div>
      <ServiceAnnouncementBar />
      <Dialog open={!!openReview} onOpenChange={(o) => !o && setOpenReview(null)}>
        <DialogContent data-testid="review-dialog" className="max-w-lg bg-[#faf7f2]">
          {openReview && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-1 mb-1">
                  {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-4 h-4 ${n <= openReview.rating ? "fill-[#d4af37] text-[#d4af37]" : "text-[#ebe6e0]"}`} />)}
                </div>
                <DialogTitle className="font-serif text-xl">{openReview.name}{openReview.city ? <span className="text-sm text-[#8a837a] font-sans capitalize"> · {openReview.city}</span> : null}</DialogTitle>
              </DialogHeader>
              <p data-testid="review-dialog-message" className="text-sm text-[#292724] leading-relaxed whitespace-pre-line">"{openReview.message}"</p>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Hero — video background OR full-width photo slideshow */}
      <section data-testid="hero" className="relative w-full">
        <div className="relative h-[60vh] min-h-[420px] lg:h-[75vh] w-full overflow-hidden bg-[#292724]">
          {heroVideo ? (
            <video
              data-testid="hero-video"
              key={heroVideo}
              autoPlay
              muted
              loop
              playsInline
              className="absolute inset-0 w-full h-full object-cover object-center"
              poster={bust(galleryList[0]?.src, galleryList[0]?.v)}
            >
              <source src={heroVideo} />
            </video>
          ) : !slidersReady ? (
            // While both /settings/gallery and /settings/hero are in flight,
            // paint a neutral placeholder so we don't briefly render a fallback
            // photo just to swap it out a moment later.
            <div data-testid="hero-slider-loading" className="absolute inset-0 bg-[#292724]" aria-hidden="true" />
          ) : (
            galleryList.map((g, i) => {
              // Only the visible slide + the next one are in the DOM; others mount as the carousel advances.
              const nextIdx = (slide + 1) % galleryList.length;
              if (i !== slide && i !== nextIdx) return null;
              return (
                <img
                  key={i}
                  src={thumb(bust(g.src, g.v), 1600)}
                  alt={g.caption}
                  loading={i === slide ? "eager" : "lazy"}
                  fetchPriority={i === slide ? "high" : "low"}
                  decoding="async"
                  className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-1000 ${i === slide ? "opacity-100" : "opacity-0"}`}
                />
              );
            })
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/20 pointer-events-none" />

          <div className="absolute inset-x-0 bottom-40 lg:bottom-36 flex flex-wrap justify-center gap-3 px-6 z-10">
            <Link
              to="/create-event"
              onClick={requestJourneyPopup}
              data-testid="hero-create-event-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#d4af37] hover:bg-[#c9a233] text-[#292724] font-medium px-7 py-3 text-sm shadow-[0_10px_30px_rgba(0,0,0,0.35)] transition-transform hover:-translate-y-0.5"
            >
              <Sparkles className="w-4 h-4" /> Create Your Event
            </Link>
            <Link
              to="/share-vision"
              data-testid="hero-share-vision-btn"
              className="inline-flex items-center gap-2 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-md border border-white/50 text-white font-medium px-7 py-3 text-sm shadow-[0_10px_30px_rgba(0,0,0,0.35)] transition-transform hover:-translate-y-0.5"
            >
              <ImagePlus className="w-4 h-4" /> Share Your Vision
            </Link>
            {heroSettings.show_partner_btn && (
              <button
                type="button"
                data-testid="hero-hire-partner-btn"
                onClick={() => setPartnerOpen(true)}
                className="partner-btn inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-serif tracking-[0.08em] text-[#f6e7b8] bg-[#1f1b16]/85 backdrop-blur-md border border-[#d4af37]/80 shadow-[0_0_0_1px_rgba(212,175,55,0.25),0_0_28px_rgba(212,175,55,0.45),0_10px_30px_rgba(0,0,0,0.35)] hover:shadow-[0_0_0_1px_rgba(212,175,55,0.5),0_0_40px_rgba(212,175,55,0.7),0_10px_30px_rgba(0,0,0,0.35)] transition-[transform,box-shadow] hover:-translate-y-0.5"
              >
                <Crown className="w-4 h-4 text-[#d4af37]" /> Hire Event Partner
              </button>
            )}
          </div>
          <Dialog open={partnerOpen} onOpenChange={setPartnerOpen}>
            <DialogContent data-testid="hire-partner-dialog" className="max-w-sm rounded-3xl border border-[#d4af37]/50 bg-[#1f1b16] text-[#fdfbf7] p-0 overflow-hidden shadow-[0_0_60px_rgba(212,175,55,0.35)]">
              <div className="h-1 w-full bg-gradient-to-r from-transparent via-[#d4af37] to-transparent" />
              <div className="px-8 pt-8 pb-9 text-center">
                <span className="mx-auto mb-4 w-12 h-12 rounded-full border border-[#d4af37]/60 flex items-center justify-center"><Crown className="w-5 h-5 text-[#d4af37]" /></span>
                <DialogHeader>
                  <DialogTitle className="eyebrow text-[#d4af37] text-[11px] text-center font-normal">Hire Event Partner</DialogTitle>
                </DialogHeader>
                <p data-testid="hire-partner-message" className="font-serif text-2xl leading-snug mt-3">This service will start from 1st&nbsp;January&nbsp;2027.</p>
                <button type="button" data-testid="hire-partner-close" onClick={() => setPartnerOpen(false)} className="mt-7 inline-flex items-center rounded-full border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-[#1f1b16] px-6 py-2 text-xs tracking-[0.15em] uppercase transition-colors">Noted</button>
              </div>
            </DialogContent>
          </Dialog>

          {!heroVideo && galleryList.length > 1 && (
            <>
              <button data-testid="slider-prev" onClick={prev} aria-label="Previous"
                className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/25 hover:bg-white/40 backdrop-blur-md text-white flex items-center justify-center transition-colors">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button data-testid="slider-next" onClick={next} aria-label="Next"
                className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/25 hover:bg-white/40 backdrop-blur-md text-white flex items-center justify-center transition-colors">
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}

          {!heroVideo && galleryList[slide]?.caption && (
            <div className="absolute bottom-24 left-0 right-0 flex justify-center">
              <div className="text-white/90 text-sm font-serif italic tracking-wide bg-black/25 backdrop-blur-sm px-4 py-1.5 rounded-full">
                {galleryList[slide]?.caption}
              </div>
            </div>
          )}

          {!heroVideo && galleryList.length > 1 && (
            <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-2">
              {galleryList.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setSlide(i)}
                  aria-label={`Slide ${i + 1}`}
                  className={`relative tap-target h-1.5 rounded-full transition-all ${i === slide ? "w-10 bg-[#d4af37]" : "w-3 bg-white/50 hover:bg-white/80"}`}
                />
              ))}
            </div>
          )}
        </div>
      </section>


      {/* Trust strip */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 -mt-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white rounded-2xl border border-[#ebe6e0] shadow-[0_20px_60px_rgba(0,0,0,0.06)] p-6 lg:p-8">
          {[
            { icon: <Sparkles className="w-5 h-5 text-[#d4af37]" />, t: "Handpicked décor", d: "Every piece curated for luxury weddings" },
            { icon: <ShieldCheck className="w-5 h-5 text-[#d4af37]" />, t: "Book Your Count", d: "Tell us the guest count and we curate the rest" },
            { icon: <Truck className="w-5 h-5 text-[#d4af37]" />, t: "Setup & dismantle", d: "Professional teams across Rajasthan" },
          ].map((f, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="p-2 rounded-full bg-[#f4efeb]">{f.icon}</div>
              <div>
                <div className="text-sm font-medium">{f.t}</div>
                <div className="text-xs text-[#8a837a] mt-0.5">{f.d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Full-width gallery slider — moved below collections */}
      {/* Collections */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
        <div className="mb-12 max-w-2xl">
          <div className="eyebrow mb-3">Collections</div>
          <h2 className="font-serif text-4xl lg:text-5xl text-[#292724] font-light gold-underline">Four worlds. One celebration.</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {COLLECTIONS.map((c) => {
            const isComingSoon = c.key === "packages";
            return (
              <Link
                key={c.key}
                to={isComingSoon ? "#" : `/category/${c.key}`}
                onClick={(e) => { if (isComingSoon) e.preventDefault(); }}
                aria-disabled={isComingSoon}
                data-testid={`collection-${c.key}`}
                className={`group relative overflow-hidden rounded-2xl block h-72 lg:h-80 ${isComingSoon ? "cursor-not-allowed" : ""}`}
              >
                {tilesLoaded ? (
                  <img
                    loading="lazy"
                    src={thumb(bust((collectionTiles[c.key] && collectionTiles[c.key].trim()) || c.img, collectionTiles._v), 600)}
                    alt={c.title}
                    className={`w-full h-full object-cover object-center transition-transform duration-700 ${isComingSoon ? "grayscale-[70%] brightness-75" : "group-hover:scale-105"}`}
                  />
                ) : (
                  // Neutral placeholder until /settings/collections resolves — never flash the stock fallback.
                  <div data-testid={`collection-${c.key}-loading`} className="w-full h-full bg-[#292724]" aria-hidden="true" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                {isComingSoon && (
                  <>
                    {/* Dim veil so visitors read this as "not yet available" */}
                    <div className="absolute inset-0 bg-[#292724]/35 backdrop-blur-[1px]" aria-hidden="true" />
                    {/* Golden diagonal ribbon */}
                    <div
                      data-testid={`collection-${c.key}-ribbon`}
                      className="absolute top-5 -right-12 rotate-45 origin-center bg-gradient-to-r from-[#b8912a] via-[#d4af37] to-[#e6c155] text-[#292724] text-[10px] font-serif tracking-[0.2em] uppercase px-14 py-1 shadow-[0_6px_16px_-4px_rgba(212,175,55,0.55)] border-t border-b border-white/40"
                    >
                      Coming Soon
                    </div>
                  </>
                )}
                <div className="absolute bottom-0 p-5">
                  <div className="eyebrow text-[#f4e5b7] mb-1 text-[10px] line-clamp-1">{c.tag}</div>
                  <div className="font-serif text-xl lg:text-2xl text-white">{c.title}</div>
                  {isComingSoon ? (
                    <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-[#d4af37]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#d4af37] animate-pulse" /> Launching soon
                    </div>
                  ) : (
                    <div className="mt-2 inline-flex items-center gap-1 text-white text-xs">
                      Explore <ArrowUpRight className="w-3 h-3 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <ServicesRow />

      {/* Below the fold: each section mounts (and fetches) only when scrolled near */}
      <LazyMount minHeight={520} onVisible={() => setBelowFold(true)}><DestinationWedding /></LazyMount>

      <LazyMount minHeight={700}><RealWeddings /></LazyMount>

      {/* Reviews / Feedback */}
      <LazyMount minHeight={600} onVisible={() => setBelowFold(true)}>
      <section data-testid="reviews-section" className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
        <div className="mb-12 max-w-2xl">
          <div className="eyebrow mb-3">Kind words</div>
          <h2 className="font-serif text-4xl lg:text-5xl text-[#292724] font-light gold-underline">
            Loved by <em className="italic text-[#d4af37]">celebrating families</em>.
          </h2>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
          {/* LEFT COLUMN — reviews slider (Instagram now has its own section below) */}
          <div className="lg:col-span-2 flex flex-col gap-3 min-w-0">
            {/* Reviews carousel — swipe/snap on mobile, gentle auto-scroll on sm+ */}
            <div className="min-w-0 w-full">
            {reviews.length === 0 ? (
              <div className="card-lux !p-8 text-center text-[#8a837a] text-sm">
                Be the first to share your celebration story below.
              </div>
            ) : (
              <div
                data-testid="reviews-carousel"
                onMouseEnter={(e) => e.currentTarget.style.setProperty("--rv-play", "paused")}
                onMouseLeave={(e) => e.currentTarget.style.setProperty("--rv-play", "running")}
                className={`${reviews.length >= 3 ? "rv-viewport" : ""} relative w-full max-w-full overflow-x-auto sm:overflow-hidden snap-x snap-mandatory pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`}
              >
                <style>{`
                  @keyframes rv-scroll { from { transform: translateX(0) } to { transform: translateX(-50%) } }
                  @media (min-width: 640px) {
                    .rv-track { animation: rv-scroll 40s linear infinite; animation-play-state: var(--rv-play, running); }
                    .rv-viewport { -webkit-mask-image: linear-gradient(to right, transparent, #000 12%, #000 88%, transparent); mask-image: linear-gradient(to right, transparent, #000 12%, #000 88%, transparent); }
                  }
                `}</style>
                <div className={`${reviews.length >= 3 ? "rv-track sm:pl-6" : ""} flex items-start gap-3 sm:gap-5 w-max px-1`}>
                  {(window.innerWidth >= 640 && reviews.length >= 3 ? [...reviews, ...reviews] : reviews).map((r, i) => (
                    <div
                      key={`${r.id}-${i}`}
                      data-testid={`review-${r.id}`}
                      className="snap-center shrink-0 w-[min(82vw,300px)] sm:w-[300px] h-[250px] sm:h-[290px] card-lux !p-4 sm:!p-6 relative flex flex-col"
                    >
                      <Quote className="absolute top-3 right-3 w-5 h-5 sm:w-6 sm:h-6 text-[#d4af37]/25" />
                      <div className="flex items-center gap-1 mb-2 sm:mb-3">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${n <= r.rating ? "fill-[#d4af37] text-[#d4af37]" : "text-[#ebe6e0]"}`} />
                        ))}
                      </div>
                      <div className="flex-1 min-h-0">
                        <p className="text-[13px] sm:text-sm text-[#292724] leading-relaxed line-clamp-4 sm:line-clamp-5">"{r.message}"</p>
                        {(r.message || "").length > 140 && (
                          <button type="button" data-testid={`review-read-more-${r.id}`} onClick={() => setOpenReview(r)}
                            className="mt-1 text-xs text-[#d4af37] hover:text-[#b8912a] font-medium">Read more</button>
                        )}
                      </div>
                      <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-[#ebe6e0] flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-serif text-sm sm:text-base truncate">{r.name}</div>
                          {r.city && <div className="text-[11px] sm:text-xs text-[#8a837a] capitalize mt-0.5 truncate">{r.city}</div>}
                        </div>
                        {r.social_url && (
                          <a
                            href={r.social_url}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`review-social-${r.id}`}
                            aria-label={`${r.name} on social media`}
                            className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white border border-[#ebe6e0] hover:border-[#d4af37] hover:text-[#d4af37] flex items-center justify-center shrink-0 transition-colors"
                          >
                            {/facebook\.com/i.test(r.social_url) ? <Facebook className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : <Instagram className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
            {(insta?.posts?.length || 0) > 0 && (
              <InstagramSection insta={insta} socialUrl={social.instagram} embedded />
            )}
        </div>

        {/* RIGHT COLUMN — full-height Leave-a-review form */}
        <form onSubmit={submitReview} data-testid="review-form" className="card-lux !p-7 h-full lg:col-span-1">
            <div className="eyebrow mb-2">Share your story</div>
            <h3 className="font-serif text-2xl mb-5">Leave a review</h3>
            <div className="space-y-3">
              <input
                data-testid="review-name"
                placeholder="Your name"
                required
                value={reviewForm.name}
                onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })}
                className="w-full border border-[#ebe6e0] rounded-lg px-3 py-2.5 text-sm"
              />
              <input
                data-testid="review-city"
                placeholder="City (optional)"
                value={reviewForm.city}
                onChange={(e) => setReviewForm({ ...reviewForm, city: e.target.value })}
                className="w-full border border-[#ebe6e0] rounded-lg px-3 py-2.5 text-sm"
              />
              <input
                data-testid="review-social-url"
                type="url"
                placeholder="Instagram / Facebook link (optional)"
                value={reviewForm.social_url}
                onChange={(e) => setReviewForm({ ...reviewForm, social_url: e.target.value })}
                className="w-full border border-[#ebe6e0] rounded-lg px-3 py-2.5 text-sm"
              />
              <div>
                <div className="text-xs text-[#8a837a] mb-2">Your rating</div>
                <div className="flex items-center gap-1.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      data-testid={`review-star-${n}`}
                      onClick={() => setReviewForm({ ...reviewForm, rating: n })}
                      className="p-1"
                      aria-label={`Rate ${n} stars`}
                    >
                      <Star className={`w-6 h-6 transition-transform hover:scale-110 ${n <= reviewForm.rating ? "fill-[#d4af37] text-[#d4af37]" : "text-[#ebe6e0]"}`} />
                    </button>
                  ))}
                </div>
              </div>
              <textarea
                data-testid="review-message"
                placeholder="Tell us about your experience…"
                required
                rows={4}
                value={reviewForm.message}
                onChange={(e) => setReviewForm({ ...reviewForm, message: e.target.value })}
                className="w-full border border-[#ebe6e0] rounded-lg px-3 py-2.5 text-sm resize-none"
              />
              {/* Photo upload */}
              <div>
                <input ref={reviewFileRef} type="file" accept="image/*" onChange={uploadReviewPhoto} className="hidden" data-testid="review-photo-file" />
                {reviewForm.photo_url ? (
                  <div className="relative rounded-lg overflow-hidden border border-[#ebe6e0]">
                    <img loading="lazy" src={reviewForm.photo_url} alt="Your celebration" className="w-full h-40 object-cover object-center" />
                    <button
                      type="button"
                      data-testid="review-photo-remove"
                      onClick={() => setReviewForm({ ...reviewForm, photo_url: "" })}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/95 hover:bg-white flex items-center justify-center shadow-md"
                      aria-label="Remove photo"
                    >
                      <X className="w-4 h-4 text-[#292724]" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    data-testid="review-photo-btn"
                    onClick={() => reviewFileRef.current?.click()}
                    disabled={photoUploading}
                    className="w-full border border-dashed border-[#d4af37]/50 hover:border-[#d4af37] bg-[#faf7f2] rounded-lg py-4 flex flex-col items-center gap-1.5 text-[#8a837a] hover:text-[#d4af37] transition-colors disabled:opacity-60"
                  >
                    <ImagePlus className="w-5 h-5" />
                    <span className="text-xs">{photoUploading ? "Uploading…" : "Add your celebration photo (optional)"}</span>
                  </button>
                )}
              </div>
              <button
                type="submit"
                data-testid="review-submit"
                disabled={submitting}
                className="btn-gold w-full justify-center disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Submit review"}
              </button>
              <p className="text-[11px] text-[#8a837a] text-center pt-1">Reviews appear after a quick approval by our team.</p>
            </div>
          </form>
        </div>

        {/* Social icons */}
        <div data-testid="social-section" className="mt-16 lg:mt-20 text-center">
          <div className="eyebrow mb-4">Follow the celebrations</div>
          <div className="flex justify-center flex-wrap gap-4">
            {social.instagram && (
              <a href={social.instagram} target="_blank" rel="noreferrer" data-testid="social-instagram" aria-label="Instagram"
                className="w-12 h-12 rounded-full bg-white border border-[#ebe6e0] hover:border-[#d4af37] hover:text-[#d4af37] flex items-center justify-center transition-all hover:shadow-lg hover:-translate-y-0.5">
                <Instagram className="w-5 h-5" />
              </a>
            )}
            {social.facebook && (
              <a href={social.facebook} target="_blank" rel="noreferrer" data-testid="social-facebook" aria-label="Facebook"
                className="w-12 h-12 rounded-full bg-white border border-[#ebe6e0] hover:border-[#d4af37] hover:text-[#d4af37] flex items-center justify-center transition-all hover:shadow-lg hover:-translate-y-0.5">
                <Facebook className="w-5 h-5" />
              </a>
            )}
            {social.whatsapp && (
              <a href={social.whatsapp} target="_blank" rel="noreferrer" data-testid="social-whatsapp" aria-label="WhatsApp"
                className="w-12 h-12 rounded-full bg-white border border-[#ebe6e0] hover:border-[#25d366] hover:text-[#25d366] flex items-center justify-center transition-all hover:shadow-lg hover:-translate-y-0.5">
                <MessageCircle className="w-5 h-5" />
              </a>
            )}
            {social.youtube && (
              <a href={social.youtube} target="_blank" rel="noreferrer" data-testid="social-youtube" aria-label="YouTube"
                className="w-12 h-12 rounded-full bg-white border border-[#ebe6e0] hover:border-[#ff0000] hover:text-[#ff0000] flex items-center justify-center transition-all hover:shadow-lg hover:-translate-y-0.5">
                <Youtube className="w-5 h-5" />
              </a>
            )}
          </div>
        </div>
      </section>
      </LazyMount>

      {/* Office location + Google Maps */}
      <LazyMount minHeight={400}><OfficeSection office={office} /></LazyMount>

      {/* CTA band */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <div className="rounded-3xl bg-[#292724] text-white p-10 lg:p-20 relative overflow-hidden">
          <div className="relative z-10 max-w-2xl">
            <div className="eyebrow text-[#d4af37] mb-4">A curator, on speed dial</div>
            <h3 className="font-serif text-3xl lg:text-5xl font-light leading-tight">Your celebration, <em className="italic text-[#f4e5b7]">handled.</em></h3>
            <p className="mt-4 text-white/80 leading-relaxed">From mandap to matra, baraat to reception — our décor curators plan every detail. Share your guest count and event city, and we handle the rest.</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/venues" data-testid="cta-view-packages" className="btn-gold">Browse packages</Link>
              <a href="https://wa.me/917297801755" className="btn-whatsapp">WhatsApp us</a>
            </div>
          </div>
          <div className="absolute -right-10 -bottom-10 w-96 h-96 rounded-full bg-[#d4af37]/20 blur-3xl" />
        </div>
      </section>
    </div>
  );
}
